import { isAxiosError } from "axios";

import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * BOM do projeto (#1345): a vigente, a prévia do arquivo do Pricing App e o
 * aplicar. Aliases sobre os tipos gerados do OpenAPI: o contrato mora no
 * backend (`routers/bom.py`).
 */
export type BomVersion = components["schemas"]["BomVersionOut"];
export type PricingAppPreview = components["schemas"]["PricingAppPreviewOut"];
export type BomVersionSummary = components["schemas"]["BomVersionSummaryOut"];

export const bomKeys = {
  /** Prefixo de tudo da BOM do projeto: gravar uma versão muda a vigente e o histórico. */
  all: (projectId: string) => ["bom", projectId] as const,
  current: (projectId: string) => ["bom", projectId, "current"] as const,
  versions: (projectId: string) => ["bom", projectId, "versions"] as const,
};

/** `GET .../bom/current`: a vigente gravada ou a calculada (`persisted: false`). 404 sem BOM. */
export async function getCurrentBom(projectId: string): Promise<BomVersion> {
  const res = await apiClient.get<BomVersion>(`/api/projects/${projectId}/bom/current`);
  return res.data;
}

/** `GET .../bom/versions`: o histórico, da mais nova para a mais antiga, com autor, total e o que cada ajuste mudou. */
export async function getBomVersions(projectId: string): Promise<BomVersionSummary[]> {
  const res = await apiClient.get<BomVersionSummary[]>(`/api/projects/${projectId}/bom/versions`);
  return res.data;
}

function fileForm(file: File, extra: Record<string, string> = {}): FormData {
  const form = new FormData();
  form.append("file", file);
  for (const [key, value] of Object.entries(extra)) form.append(key, value);
  return form;
}

/** `POST .../bom/pricing-app/preview`: a conciliação, sem gravar. Arquivo recusado é 422 com os motivos. */
export async function previewPricingApp(projectId: string, file: File): Promise<PricingAppPreview> {
  // Sem o cabeçalho, o axios transforma o FormData em JSON e o arquivo some.
  const res = await apiClient.post<PricingAppPreview>(
    `/api/projects/${projectId}/bom/pricing-app/preview`,
    fileForm(file),
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return res.data;
}

/**
 * `POST .../bom/pricing-app/apply`: grava a versão do arquivo como a vigente.
 * `baseVersion` é a vigente da prévia (0 sem nenhuma); se outra versão foi
 * gravada desde então, 409.
 */
export async function applyPricingApp(
  projectId: string,
  file: File,
  baseVersion: number,
  actions?: Record<string, string>,
): Promise<BomVersion> {
  const extra: Record<string, string> = { base_version: String(baseVersion) };
  // RF-04: só o que a pessoa trocou na prévia; o resto fica com a ação padrão.
  if (actions && Object.keys(actions).length) extra.actions = JSON.stringify(actions);
  const res = await apiClient.post<BomVersion>(
    `/api/projects/${projectId}/bom/pricing-app/apply`,
    fileForm(file, extra),
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
  );
  return res.data;
}

export type BomAdjustment = components["schemas"]["BomAdjustmentIn"];

/** `POST .../bom/versions/{v}/simulate`: a versão com os ajustes, sem gravar. */
export async function simulateBomAdjustment(
  projectId: string,
  version: number,
  adjustments: BomAdjustment[],
): Promise<BomVersion> {
  const res = await apiClient.post<BomVersion>(
    `/api/projects/${projectId}/bom/versions/${version}/simulate`,
    { adjustments },
  );
  return res.data;
}

/**
 * `POST .../bom/versions`: salva o ajuste como versão nova, que vira a vigente.
 * Base desatualizada é 409; ajuste sem justificativa, 422.
 */
export async function saveBomAdjustment(
  projectId: string,
  body: { base_version: number; adjustments: BomAdjustment[]; note?: string | null },
): Promise<BomVersion> {
  const res = await apiClient.post<BomVersion>(`/api/projects/${projectId}/bom/versions`, body);
  return res.data;
}

function attachmentName(header: unknown, fallback: string): string {
  const value = typeof header === "string" ? header : "";
  const plain = /filename="?([^";]+)"?/i.exec(value);
  return plain ? plain[1] : fallback;
}

/** FileReader, e não `Blob.text()`: o jsdom dos testes não tem o segundo. */
function blobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * `GET .../bom/versions/{v}/export?format=pricing_app`: baixa o arquivo do
 * Pricing App de onde a versão veio, com as quantidades dela (B5). Versão sem
 * arquivo de origem, ou com o original em .xlsx, é 409 com o motivo.
 */
export async function downloadBomVersionPricingApp(projectId: string, version: number): Promise<void> {
  let res;
  try {
    res = await apiClient.get<Blob>(`/api/projects/${projectId}/bom/versions/${version}/export`, {
      params: { format: "pricing_app" },
      responseType: "blob",
      timeout: 120_000,
    });
  } catch (err) {
    // Com responseType blob, o corpo da recusa também vem em Blob: sem ler o
    // JSON de dentro, a tela não teria o motivo que o backend deu.
    if (isAxiosError(err) && err.response?.data instanceof Blob) {
      try {
        err.response.data = JSON.parse(await blobText(err.response.data));
      } catch {
        // Corpo que não é JSON segue como veio, e a tela mostra a mensagem genérica.
      }
    }
    throw err;
  }
  const name = attachmentName(res.headers?.["content-disposition"], `pricing-app-v${version}.xls`);
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.ms-excel" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
